import { Component, OnInit, computed, inject, signal, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbService, CslBreadcrumbItem } from '@card-software/library';
import { Translation } from '../../models/Translations';
import {
  InfoBranch,
  InfoLink,
  InfoPage,
  InfoStep,
  InfoStepVariant,
  stepContentForBranch,
  stepDiffersForBranch,
} from '../../models/info/info-content';
import { DEFAULT_INFO_SLUG, INFO_GROUPS, INFO_PAGES, INFO_PARENT_LABELS } from '../../models/info/info-pages';
import { LocaleHelperService } from '../../services/locale-helper.service';

/** One row of the "what changes with the base" matrix. */
interface MatrixRow {
  title: string;
  /** Per branch, in the page's branch order: does this step differ there? */
  differs: boolean[];
}

/**
 * The Info section (#4894) — an in-app guide to how the finance flows are actually run.
 *
 * <p>The component renders whatever {@link INFO_PAGES} describes and holds no prose of its own, so the
 * guide is corrected by editing content, not markup. Built out of stock PrimeNG (PanelMenu, Tabs, Card,
 * Panel, Table, Tag) so the section carries almost no styling of its own.</p>
 *
 * <p>Which steps "differ" per branch is derived from the content — a step differs for a branch exactly
 * when it defines a variant for it. The badge on a step and the comparison matrix read that same
 * function, so they cannot drift apart.</p>
 */
@Component({
  selector: 'app-info',
  templateUrl: './info.component.html',
  styleUrl: './info.component.scss',
})
export class InfoComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly locale = inject(LocaleHelperService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  /**
   * The section's own chrome, kept bilingual here rather than in translations.json.
   *
   * <p>The articles carry their language with them ({@link Translation} per string), and splitting the
   * chrome into the global bundle would leave half of one page translated and half not the moment either
   * side fell behind. Everything the Info section says lives in the Info section.</p>
   */
  readonly ui = {
    filter: { en: 'Filter', es: 'Filtrar' },
    sections: { en: 'Sections', es: 'Secciones' },
    scribe: { en: 'Scribe walkthrough', es: 'Guía en Scribe' },
    badgeSame: { en: 'Same for all types', es: 'Igual en todos los tipos' },
    badgeDiffers: { en: 'Differs for {branch}', es: 'Cambia para {branch}' },
    conditions: { en: 'Conditions', es: 'Condiciones' },
    matrixTitle: { en: 'What changes with the base', es: 'Qué cambia según la base' },
    matrixStep: { en: 'Step', es: 'Paso' },
    legendSame: { en: 'same', es: 'igual' },
    legendDiffers: { en: 'differs', es: 'cambia' },
    pending: {
      en: 'This branch differs here, but the text has not been written yet.',
      es: 'Esta rama cambia aquí, pero el texto aún no se ha redactado.',
    },
    noResults: { en: 'No pages match.', es: 'Ninguna página coincide.' },
  } satisfies Record<string, Translation>;

  readonly slug = signal<string>(DEFAULT_INFO_SLUG);
  /** Which way of starting the flow the reader is looking at. Reset whenever the page changes. */
  readonly activeBranchId = signal<string | null>(null);
  readonly filterText = signal<string>('');

  readonly page = computed<InfoPage | null>(() => INFO_PAGES.find((p) => p.slug === this.slug()) ?? null);

  readonly branches = computed<InfoBranch[]>(() => this.page()?.branches ?? []);
  readonly steps = computed<InfoStep[]>(() => this.page()?.steps ?? []);
  readonly isFlowPage = computed(() => this.branches().length > 0);

  readonly activeBranch = computed<InfoBranch | null>(() => {
    const branches = this.branches();
    if (branches.length === 0) return null;
    return branches.find((b) => b.id === this.activeBranchId()) ?? branches[0];
  });

  /** Breadcrumb for the shell header: the page's trail plus its own title as the current item. */
  readonly menuItems = computed<CslBreadcrumbItem[]>(() => {
    const page = this.page();
    if (!page) return [];
    return [...page.breadcrumb.map((crumb) => ({ label: this.text(crumb) })), { label: this.text(page.title) }];
  });

  private readonly syncBreadcrumb = effect(() => this.breadcrumbService.set(this.menuItems()), { allowSignalWrites: true });

  /**
   * The comparison matrix. Derived from the steps, never authored — a hand-kept table would start lying
   * the first time a step gained or lost a variant.
   */
  readonly matrixRows = computed<MatrixRow[]>(() => {
    const branches = this.branches();
    return this.steps().map((step) => ({
      title: this.text(step.title),
      differs: branches.map((branch) => stepDiffersForBranch(step, branch.id)),
    }));
  });

  /** Sidebar, as a PanelMenu model: one panel per group, pages beneath, nested under their parent heading. */
  readonly navModel = computed<MenuItem[]>(() => {
    const needle = this.filterText().trim().toLowerCase();
    const matches = (page: InfoPage) =>
      !needle || this.text(page.navLabel).toLowerCase().includes(needle) || this.text(page.title).toLowerCase().includes(needle);

    return INFO_GROUPS.map((group): MenuItem | null => {
      const pages = INFO_PAGES.filter((page) => page.group === group.id && matches(page));
      if (pages.length === 0) return null;

      // Pages that hang off a heading (Tax Authorization under Provider Details) are nested beneath it.
      // The heading is a screen in the app rather than an article, so it is not itself clickable.
      const roots = pages.filter((page) => !page.parentSlug);
      const parentSlugs = Array.from(new Set(pages.filter((page) => page.parentSlug).map((page) => page.parentSlug!)));

      const items: MenuItem[] = [
        ...roots.map((page) => this.toNavItem(page)),
        ...parentSlugs.map((parentSlug) => ({
          label: this.text(INFO_PARENT_LABELS[parentSlug] ?? { en: parentSlug, es: parentSlug }),
          icon: 'pi pi-building',
          items: pages.filter((page) => page.parentSlug === parentSlug).map((page) => this.toNavItem(page)),
        })),
      ];

      return { label: this.text(group.label), items, expanded: true };
    }).filter((item): item is MenuItem => item !== null);
  });

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const slug = params.get('slug');
      // An unknown slug lands on the first page rather than an empty shell — the guide always has
      // something to show, and a dead link here is a typo, not a missing record.
      const resolved = slug && INFO_PAGES.some((page) => page.slug === slug) ? slug : DEFAULT_INFO_SLUG;
      this.slug.set(resolved);
      this.activeBranchId.set(this.page()?.branches?.[0]?.id ?? null);
    });
  }

  private toNavItem(page: InfoPage): MenuItem {
    return {
      label: this.text(page.navLabel),
      icon: page.branches?.length ? 'pi pi-directions' : 'pi pi-check-circle',
      command: () => this.openPage(page.slug),
      styleClass: page.slug === this.slug() ? 'cs-nav-rail-active' : undefined,
    };
  }

  /** Resolves a bilingual string for the current language. */
  text(value: Translation | null | undefined): string {
    return this.locale.getLocalizedValue(value);
  }

  openPage(slug: string): void {
    this.router.navigate(['/info', slug]);
  }

  selectBranch(branchId: string): void {
    this.activeBranchId.set(branchId);
  }

  // ── Step rendering ──────────────────────────────────────────────────────────
  /** The content a step shows for the branch on screen. */
  contentFor(step: InfoStep): InfoStepVariant {
    return stepContentForBranch(step, this.activeBranch()?.id ?? '');
  }

  /** Whether the step differs for the branch on screen — drives its badge. */
  differsHere(step: InfoStep): boolean {
    return stepDiffersForBranch(step, this.activeBranch()?.id ?? '');
  }

  stepBadge(step: InfoStep): string {
    if (!this.differsHere(step)) {
      return this.text(this.ui.badgeSame);
    }
    return this.text(this.ui.badgeDiffers).replace('{branch}', this.text(this.activeBranch()?.label));
  }

  // ── Links ───────────────────────────────────────────────────────────────────
  followLink(link: InfoLink): void {
    if (link.externalUrl) {
      window.open(link.externalUrl, '_blank');
      return;
    }
    if (link.route) {
      this.router.navigate([link.route]);
    }
  }

  openScribe(): void {
    const url = this.page()?.scribeUrl;
    if (url) window.open(url, '_blank');
  }
}
